/**
 * MKS-212 → MKS-345 — the lead pipeline used to be this module's business: a
 * frozen seven-entry tuple that had to be kept in step by hand with
 * `VALID_STAGES` in the backend's `leadService.ts`.
 *
 * It is configuration now (`lead_stages`, edited from the board header and
 * under Admin), so every surface reads the stage list off the `GET /api/leads`
 * payload — `stages`, already in board order — instead of importing a
 * constant. What is left here is the small amount of shared derivation the
 * board, the two pages and the external board would otherwise each repeat.
 */
import type { Lead, LeadStage } from '../../services/api/leads';

/** Group leads into their columns, preserving the server's order within each. */
export function groupLeadsByStage(
  leads: Lead[],
  stages: readonly LeadStage[],
): Record<number, Lead[]> {
  const map: Record<number, Lead[]> = {};
  for (const stage of stages) map[stage.id] = [];
  for (const lead of leads) {
    // A lead whose stage is not in the list (a column deleted between two
    // requests) is dropped rather than rendered into a column that is not
    // there; the next refetch brings both sides back in step.
    map[lead.stageId]?.push(lead);
  }
  return map;
}

/**
 * MKS-345 — staleness only ever applied to deals still in play. That used to
 * read `stage === 'won' || stage === 'lost'`; it reads the stage's kind now,
 * so a renamed or a newly added closing column behaves the same.
 */
export function isClosedLead(lead: Pick<Lead, 'stageKind'>): boolean {
  return lead.stageKind !== 'open';
}

/** The won stage of the pipeline, or null while the list is still loading. */
export function wonStage(stages: readonly LeadStage[]): LeadStage | null {
  return stages.find((s) => s.kind === 'won') ?? null;
}
